import { HttpStatusCode } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { SubmitHandler, useForm, Controller } from 'react-hook-form';
import { ClipLoader } from 'react-spinners';
import useApi from '../../../utils/AxiosWrapper';
import { useUser } from '../../../utils/UserContext';
import { Application } from '../../../types/dbTypes';
import {
  AddressComponents,
  isCompleteAddress,
} from '../../../utils/AddressUtils';
import { rejectIncompleteAddress } from '../../../utils/incompleteAddressGuard';
import SignupFooter from '../SignupFooter';
import { setApplicationStep } from '../../../pages/signup';
import {
  ApplicationStatus,
  ApplicationStep,
  Entity,
  User,
} from '../../../types/apiTypes';
import { useScreenView } from '../../../utils/ScreenViewContext';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { InputNumber, Radio, Select } from 'antd';
import { US_STATES } from '../../../types/constants';
import { Question } from '@phosphor-icons/react';
import Tooltip from '../../dashboard/customers/tabs/Tooltip';
import {
  formatCurrencyForAntd,
  parseCurrencyFromAntd,
} from '../../../utils/currencyFormat';
import SensitiveFieldHelp from '../SensitiveFieldHelp';
import { formatUSPhonePretty, parseUSPhoneToE164 } from '../../../utils/phone';
import TrustSignals from '../TrustSignals';
import useFrontChat from '../../../hooks/useFrontChat';
import {
  claimStepCompletionReport,
  trackApplicationStepCompleted,
} from '../../../utils/ga4';
import {
  trackMetaConversionServerSide,
  trackMetaLead,
} from '../../../utils/metaConversion';
import FlexibleAddressInput from './FlexibleAddressInput';
import {
  safeGetItem,
  safeRemoveItem,
  safeSessionGetItem,
  safeSessionRemoveItem,
  safeSessionSetItem,
} from '../../../shared/safeStorage';
import {
  applyCarrierPrefill,
  hasText,
  normalizeStoredPhone,
} from '../../../utils/carrierPrefill';
import {
  buildPrefillAccountSnapshot,
  carrierAlreadyLookedUp,
  carrierPrefillIsCurrent,
  carrierPrefillKey,
  fetchCarrierPrefillOnce,
  markCarrierLookedUp,
} from '../../../utils/carrierPrefillRequest';
import {
  CARRIER_IDENTITY_FORM_FIELDS,
  CARRIER_NUMBER_ERROR_STORAGE_KEY,
  customerHasCarrierIdentity,
  readSavedCarrierIdentity,
  SavedCarrierIdentity,
} from '../../../utils/carrierIdentity';
import {
  BUSINESS_INFO_STORAGE_KEY,
  HEADS_UP_ENABLED,
  PREFILL_LOOKUP_LOADING_MAX_MS,
  PREFILL_LOOKUP_LOADING_TEXT,
} from '../../../constants/headsUp';

interface BusinessInfoProps {
  currStep: string;
  selectedRole: Entity;
  setStep: (newStep: string) => void;
  application: Application;
  setApplication: (application: Application) => void;
  /**
   * Sends the customer back to the heads-up page to give us a different MC/DOT (THT-711).
   *
   * A correction is not an edit of one field — the number drives a carrier lookup the customer
   * has to confirm by name, and then a fresh prefill. Doing that inline would mean rebuilding
   * both on this step; handing them back to the page that already does it means a corrected
   * number goes through exactly the same verification as the first one.
   *
   * Optional so the step still renders standalone in tests and for roles that never see it.
   */
  onEditCarrierIdentity?: () => void;
}

export type BusinessInfoForm = {
  businessName: string;
  businessPhone: string;
  businessAddress: string;
  taxId: string;
  annualRevenue: number;
  partnerCompany: string;
  yearsInBusiness: string;
  referralCode: string;
  state_of_registration: string;
  address_st_number?: string;
  address_st_name?: string;
  address_unit?: string;
  address_city?: string;
  address_state?: string;
  address_zip?: string;
  mc_number?: string;
  dot_number?: string;
  carrier_type?: 'MC' | 'DOT';
  owns_their_mc_dot_number?: boolean | null;
  other_mc_dot_number_owner_name?: string;
  // Factoring company question (relocated here from the removed Business Debt step).
  uses_factoring?: 'yes' | 'no';
  factoring_company_name?: string;
};

/** The step's own session draft, or an empty object when absent or unreadable. */
const readStoredBusinessInfo = (): Partial<BusinessInfoForm> => {
  try {
    const parsed = JSON.parse(
      safeSessionGetItem(BUSINESS_INFO_STORAGE_KEY) || '{}'
    );
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

/**
 * A stored draft with the one-line address dropped when the parts behind it are incomplete.
 *
 * Both restores — the mount hydration and the read-back after the prefill lands — go through
 * here, so neither can put an address in the bar that the dropdown check will then refuse. A
 * field that looks answered and mysteriously fails submit is worse than an empty one that asks
 * for a selection, and a session can still hold a partial one-liner written before its writers
 * learned this rule.
 *
 * Only the composed field is dropped. Every part we hold is still restored, so nothing is lost —
 * the customer sees what we know the moment they open the manual fields.
 */
const restorableBusinessInfo = (
  stored: Partial<BusinessInfoForm>
): Partial<BusinessInfoForm> => {
  if (isCompleteAddress(stored)) return stored;
  const { businessAddress, ...withoutComposedAddress } = stored;
  return withoutComposedAddress;
};

/**
 * The form fields that make up the address — the parts and the one-line bar composed from them.
 * A late prefill treats them as one unit; see the read-back that applies it.
 */
const ADDRESS_FORM_FIELDS: (keyof BusinessInfoForm)[] = [
  'address_st_number',
  'address_st_name',
  'address_unit',
  'address_city',
  'address_state',
  'address_zip',
  'businessAddress',
];

function BusinessInfo({
  currStep,
  selectedRole,
  setStep,
  application,
  setApplication,
  onEditCarrierIdentity,
}: BusinessInfoProps) {
  // Initialize Front chat widget
  useFrontChat();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    getValues,
    clearErrors,
    unregister,
    formState: { errors },
    control,
  } = useForm<BusinessInfoForm>();
  const { setUserRecord, userId, userRecord } = useUser();
  const navigate = useNavigate();
  const { callProtectedApi } = useApi();
  const { screenView } = useScreenView();
  // The identity the customer authenticated with — the account-tier fallback for a customer row
  // that carries no email of its own. See `buildPrefillAccountSnapshot`.
  const { user } = useAuth0();
  const [loading, setLoading] = useState(false);
  const [addressSelectedFromDropdown, setAddressSelectedFromDropdown] =
    useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [carrierType, setCarrierType] = useState<'MC' | 'DOT'>('MC');
  const [carrierNumberWarning, setCarrierNumberWarning] =
    useState<boolean>(false);
  // The single read of the bounce-back flag; the effect below only clears it. Sticky for the life
  // of the page, unlike the warning banner, which typing dismisses: once the backend has rejected
  // the saved identifier this form has to keep offering the fields that fix it, or the bounce-back
  // from the Review transition has nothing to correct.
  const [carrierNumberNeedsCorrection] = useState(
    () => safeSessionGetItem(CARRIER_NUMBER_ERROR_STORAGE_KEY) === 'true'
  );
  // THT-729: the heads-up page asks for the carrier identity right after login, so this form
  // only asks when that answer is missing — a legacy customer, or someone who came through the
  // heads-up "Skip for now" save-failure path — or when the saved answer turned out to be wrong.
  //
  // Latched the first time a customer record actually arrives, for two reasons that used to need
  // an effect and a submit ref between them: the record can still be null on the first render, and
  // our own successful PATCH writes the carrier number back into it — re-reading after that would
  // flip the gate and delete the block out from under a customer who is still on this page, which
  // is exactly where a failed /applications/upsert leaves them. Asked and answered once.
  //
  // Gated on HEADS_UP_ENABLED alongside the page that feeds it: with the switch off nothing
  // collects the identity up front, so slimming the form down here would take the MC/DOT field
  // away with nothing having asked for it. The two move together, in both directions.
  const collectedWhenRecordArrived = useRef<boolean | null>(null);
  /**
   * The identity itself, latched in the same moment and for the same reason: it is what the
   * summary reads out and what seeds the block back if the customer asks to change it, and our
   * own successful PATCH rewrites the record underneath. Taken from the version of the record
   * that decided the gate, so the two can never describe different numbers.
   */
  const identityWhenRecordArrived = useRef<SavedCarrierIdentity | null>(null);
  /**
   * Whether this mount is going to go and fetch the prefill, decided in the same latch so the
   * loading state can be on screen from the FIRST render that knows it — deciding in the effect
   * instead would paint one frame of the empty form before replacing it, which is the flicker
   * this whole thing exists to remove.
   */
  const willRefetchPrefill = useRef(false);
  /**
   * Whether that fetch is worth WAITING for, which is a narrower question than whether to make
   * it. A customer who already has answers on this step — a draft in the session, or their own
   * record hydrated by `populateApplicationData` — has nothing to wait for: those values outrank
   * Highway anyway, so a loading state would hold a form that is already filled in. The lookup
   * still goes out to fill whatever is blank; it just does it behind the form.
   */
  const waitForPrefill = useRef(false);
  if (collectedWhenRecordArrived.current === null && userRecord) {
    collectedWhenRecordArrived.current =
      HEADS_UP_ENABLED && customerHasCarrierIdentity(userRecord);
    const saved = readSavedCarrierIdentity(userRecord);
    identityWhenRecordArrived.current = saved;
    const identifier = saved?.mc_number || saved?.dot_number;
    /**
     * A leased-on driver is the one holder of an identifier we must NOT look up (THT-752). The
     * number on their record is the authority they run under — somebody else's carrier — so
     * Highway would answer with that carrier's legal name, address, contact and trucks, and the
     * prefill would push all of it into the form asking about THEIR business. The heads-up page
     * already declines to fetch on this path for exactly this reason; before THT-752 these
     * records carried no identifier at all, so this side never had to say it.
     */
    const runsUnderSomeoneElsesAuthority =
      saved?.owns_their_mc_dot_number === false;
    willRefetchPrefill.current = Boolean(
      HEADS_UP_ENABLED &&
      saved &&
      identifier &&
      !runsUnderSomeoneElsesAuthority &&
      !carrierAlreadyLookedUp(carrierPrefillKey(identifier, saved.carrier_type))
    );
    /**
     * The legal name is the anchor: the prefill always writes it when Highway knows the carrier,
     * so its presence means this step already has something to show.
     *
     * Read from the RECORD as well as the session draft. `populateApplicationData` is what copies
     * the record into that draft, and it runs in an effect gated on the customer having revenue
     * on file — while this is a render-time read that happens first. Testing only the draft
     * therefore showed a four-second spinner to a customer whose own business name was sitting in
     * the record all along, waiting on a lookup that could not tell them anything they did not
     * already have. Asking the record directly makes the ordering moot rather than racing it.
     */
    const draft = readStoredBusinessInfo();
    const recordBusinessName = (userRecord as { business_name?: string })
      ?.business_name;
    waitForPrefill.current =
      willRefetchPrefill.current &&
      !hasText(draft.businessName) &&
      !hasText(recordBusinessName);
  }
  /**
   * What the prefill effect reads at FIRE time instead of depending on.
   *
   * `useApi()` builds a new `callProtectedApi` on every render — it is not wrapped in a
   * `useCallback` — so listing it as a dependency re-entered the effect, and issued another
   * upstream lookup, on every ordinary re-render. The record and the Auth0 identity move for
   * their own reasons and have the same effect. Held in refs, they stay current for the one
   * lookup that does go out without deciding when it goes out.
   */
  const callProtectedApiRef = useRef(callProtectedApi);
  callProtectedApiRef.current = callProtectedApi;
  const userRecordRef = useRef(userRecord);
  userRecordRef.current = userRecord;
  const authIdentityRef = useRef(user);
  authIdentityRef.current = user;

  /**
   * Flipped once the lookup has landed, failed, or outstayed its welcome — see
   * PREFILL_LOOKUP_LOADING_MAX_MS. Only ever gates the loading state; the fetch itself carries on
   * regardless and still fills whatever is empty when it arrives.
   */
  const [prefillSettled, setPrefillSettled] = useState(false);
  const showPrefillLoader = waitForPrefill.current && !prefillSettled;
  const carrierIdentityAlreadyCollected =
    collectedWhenRecordArrived.current === true &&
    !carrierNumberNeedsCorrection;
  // Labels for the invalid-carrier warning, keyed off the carrier type the user selected.
  const carrierNumberLabel = carrierType === 'MC' ? 'MC Number' : 'DOT Number';
  const otherCarrierNumberLabel =
    carrierType === 'MC' ? 'DOT Number' : 'MC Number';

  /**
   * What the summary reads out in place of the carrier block (THT-711).
   *
   * The lessee's line names the authority they run under — the answer they actually gave.
   * Everyone else sees the identifier itself: the point of showing it at all is that a customer
   * can only catch a typo in a number they can see.
   *
   * NOTE (THT-752): that rule now has a gap. This branch was written when a lessee had no number
   * to show; the heads-up No path saves one, so their line names the authority while hiding the
   * identifier the Review transition actually validates. It is not a dead end — a rejected number
   * comes back through `carrierNumberNeedsCorrection`, which renders the editable block rather
   * than this summary — but a lessee cannot proactively check the number for a typo the way an
   * owner-operator can. Showing both is a user-facing copy change and wants John's eye, so it is
   * deliberately not made here.
   */
  const savedIdentity = identityWhenRecordArrived.current;
  const runsUnderAnotherAuthority =
    savedIdentity?.owns_their_mc_dot_number === false;
  const savedIdentityLabel = runsUnderAnotherAuthority
    ? 'Running under'
    : `${savedIdentity?.carrier_type ?? 'MC'} Number`;
  const savedIdentityValue = runsUnderAnotherAuthority
    ? savedIdentity?.other_mc_dot_number_owner_name ||
      "someone else's authority"
    : savedIdentity?.mc_number || savedIdentity?.dot_number || '';

  /**
   * The carrier this mount will look up, as something the prefill effect can DEPEND on.
   *
   * `null` until the customer record has arrived and the latch above has decided, then a stable
   * string that never changes again, because the identity ref is written exactly once. That
   * matters: dropping `userRecord` from the effect's dependencies is what stops a lookup per
   * render, but it also removed the only thing that re-entered the effect when the record showed
   * up AFTER the first render. The effect would run once against an undecided latch and never
   * again, and the lookup would silently never happen at all.
   *
   * Deliberately NOT derived from `willRefetchPrefill`, which the effect consumes on entry: a
   * dependency that flipped when the fetch started would tear the effect down mid-flight and
   * abandon the response it had just asked for.
   */
  const savedIdentifier = savedIdentity?.mc_number || savedIdentity?.dot_number;
  const carrierLookupKey =
    savedIdentity && savedIdentifier
      ? carrierPrefillKey(savedIdentifier, savedIdentity.carrier_type)
      : null;

  const onSubmit: SubmitHandler<BusinessInfoForm> = (data) => {
    const e164 = parseUSPhoneToE164(data.businessPhone);
    if (!e164) {
      toast.error('Please enter a valid US phone number.');
      return;
    }
    data.businessPhone = e164;
    // The last check before the API: whichever way the address got into the form (a Places pick,
    // the manual fields, or a restored draft), the request only goes out with every part the
    // backend requires. `patchCustomer` is only reachable from here.
    if (rejectIncompleteAddress(data, setError, 'businessAddress')) return;

    // The API takes E.164; the session copy is what this step and Review render back, so it keeps
    // the formatted number the customer was just looking at rather than flipping the field to raw
    // digits the moment they step back into it.
    safeSessionSetItem(
      BUSINESS_INFO_STORAGE_KEY,
      JSON.stringify({ ...data, businessPhone: formatUSPhonePretty(e164) })
    );
    patchCustomer(data, userId ?? '');
  };

  const handleSetLoading = (loading: boolean) => {
    setLoading(loading);
  };

  // Factoring company name only persists when the customer says they use a factoring company.
  const resolveFactoringCompanyName = (
    businessData: BusinessInfoForm
  ): string | null =>
    businessData?.uses_factoring !== 'no' &&
    businessData?.factoring_company_name?.trim()?.length
      ? businessData.factoring_company_name.trim()
      : null;

  const createApplicationRecord = async (
    businessData: BusinessInfoForm,
    customer_id: string
  ) => {
    setLoading(true);
    try {
      const response = await callProtectedApi(
        '/applications/upsert',
        'post',
        {
          customer_id: customer_id,
          average_annual_revenue: Number(businessData?.annualRevenue),
          years_in_business: Number(businessData?.yearsInBusiness),
          referral_code_used:
            businessData?.referralCode && businessData?.referralCode?.length > 0
              ? businessData?.referralCode
              : undefined,
          // Factoring company relocated here from the removed Business Debt step. Sent on the
          // business_info step (backend allows factoring_company_name on business_info).
          factoring_company_name: resolveFactoringCompanyName(businessData),
          application_step: 'business_info',
        },
        'Error occurred while creating your application.'
      );
      const data = response?.data?.data?.application;
      if (
        data &&
        Object.entries(data)?.length > 0 &&
        (response?.status === HttpStatusCode.Ok ||
          response?.status === HttpStatusCode.Created)
      ) {
        setApplication(data as Application);

        // Clear tachref from localStorage after successful application creation
        // This prevents it from being reused in future signups
        if (businessData?.referralCode) {
          safeRemoveItem('tachref');
        }

        const step = await setApplicationStep({
          step: ApplicationStep.PersonalInfo,
          application_id: data?.application_id,
          customer_id: customer_id,
          callProtectedApi,
          setLoading: handleSetLoading,
          previousStep: ApplicationStep.BusinessInfo,
          applicationStatus: data?.status as ApplicationStatus,
        });
        if (step?.length > 0 && step === ApplicationStep.PersonalInfo) {
          // The carrier has cleared screen one — Brittany's 00 -> 01, and the moment both
          // platforms optimize on. Fired here rather than on the screen-two render so it
          // reflects a completed step rather than a page view.
          //
          // NOTE: `PersonalInfo` comes AFTER `BusinessInfo` in the flow. The ApplicationStep
          // enum happens to declare it first, but nothing consumes that ordering — the order
          // is set by these explicit setApplicationStep calls.
          const applicationId = data?.application_id;
          // Once per application, across remounts — this component is unmounted the moment the
          // step advances, so the claim cannot live in a ref here. See claimStepCompletionReport.
          if (applicationId && claimStepCompletionReport(applicationId)) {
            trackApplicationStepCompleted({
              completedStep: ApplicationStep.BusinessInfo,
              applicationId,
            });
            // Meta would absorb a repeat on its own (its event id is derived from the
            // application id, with no timestamp) but there is no reason to spend the call.
            trackMetaLead({ applicationId });
            trackMetaConversionServerSide({
              applicationId,
              eventName: 'Lead',
              callProtectedApi,
            });
          }

          setStep(ApplicationStep.PersonalInfo);
        }
      } else if (response?.status === HttpStatusCode.Conflict) {
        const decision = response?.data?.data?.decision;
        if (decision === 'rejected') {
          setStep('rejected');
        } else if (decision === 'approved') {
          toast.success(
            'Your application has already been approved. Redirecting you to the dashboard.'
          );
          navigate('/dashboard/home');
        }
      }
    } catch (e: any) {
      console.error('Error occurred while creating application record: ', e);
    }
  };

  const patchCustomer = async (
    businessData: BusinessInfoForm,
    customer_id: string
  ) => {
    setLoading(true);
    try {
      const normalizedAddressFields: AddressComponents = {
        address_st_number: businessData.address_st_number,
        address_st_name: businessData.address_st_name,
        address_unit: businessData.address_unit,
        address_city: businessData.address_city,
        address_state: businessData.address_state,
        address_zip: businessData.address_zip,
      };

      const response = await callProtectedApi(
        `/customers/${customer_id}`,
        'patch',
        {
          ...normalizedAddressFields,
          // address line 2 (unit) is a nullable column — omit it when empty (the backend stores
          // null) instead of sending a "-" placeholder. Sent as undefined so it's dropped from the
          // payload: the customers PATCH Joi schema is joi.string().optional() and rejects an
          // explicit null, and the handler coalesces the absent value to null.
          address_unit:
            normalizedAddressFields.address_unit?.trim() || undefined,
          phone_number: businessData?.businessPhone,
          business_name: businessData?.businessName,
          employer_id_number: businessData?.taxId,
          state_of_registration: businessData?.state_of_registration,
          // Carrier identity is only sent when this form actually asked for it. Once the
          // heads-up page has captured it, every carrier-identity key is left out of the
          // payload entirely so a saved MC/DOT — or an authority-"No" answer, which saves an
          // owner name and no identifier at all — is never overwritten with blanks.
          ...(carrierIdentityAlreadyCollected
            ? {}
            : {
                motor_carrier_number: businessData?.mc_number
                  ? businessData?.mc_number
                  : undefined,
                dot_number: businessData?.dot_number
                  ? businessData?.dot_number
                  : undefined,
                owns_their_mc_dot_number:
                  businessData?.owns_their_mc_dot_number,
                // If they don't own the MC/DOT number, we need to send the owner name
                other_mc_dot_number_owner_name:
                  !businessData?.owns_their_mc_dot_number
                    ? businessData?.other_mc_dot_number_owner_name
                    : undefined,
              }),
        },
        'Error occurred while updating your business info.'
      );

      // Handle duplicate phone number or email errors
      if (response?.status === HttpStatusCode.Conflict) {
        const field = response?.data?.data?.field;
        if (field === 'phone_number') {
          toast.error(
            'This phone number is already associated with an existing account. Please use a different number or sign in to your existing account.',
            { duration: 7000 }
          );
          return;
        }
      }

      const data = response?.data?.data?.customer;
      if (
        data &&
        Object.entries(data)?.length > 0 &&
        (response?.status === HttpStatusCode.Ok ||
          response?.status === HttpStatusCode.Created)
      ) {
        await createApplicationRecord(businessData, customer_id);
        setUserRecord({
          ...data,
          factoring_company_name: resolveFactoringCompanyName(businessData),
          userType: 'customer',
        } as User);
      }
    } catch (e: any) {
      console.error(
        "Error occurred while updating customer's business info: ",
        e
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Pre-fill the factoring company from the customer record (e.g. a returning applicant) when
    // there's no in-session form data to restore below.
    if (safeSessionGetItem(BUSINESS_INFO_STORAGE_KEY)) return;
    const savedFactoring = (userRecord as any)?.factoring_company_name;
    if (savedFactoring) {
      setValue('uses_factoring', 'yes');
      setValue('factoring_company_name', savedFactoring);
    }
  }, [userRecord, setValue]);

  useEffect(() => {
    // The flag itself was read once, above, into `carrierNumberNeedsCorrection` — which is what
    // keeps the carrier fields on screen even when the heads-up page already collected an answer,
    // since the number on file is the one that failed. All that is left here is raising the
    // dismissible banner and clearing the flag so it fires once.
    if (carrierNumberNeedsCorrection) {
      setCarrierNumberWarning(true);
      safeSessionRemoveItem(CARRIER_NUMBER_ERROR_STORAGE_KEY);
    }

    // Retrieve form data from session storage
    const savedData = safeSessionGetItem(BUSINESS_INFO_STORAGE_KEY);
    const tachref = safeGetItem('tachref');
    if (tachref) {
      setValue('referralCode', tachref);
    }
    if (savedData) {
      const parsedData: Partial<BusinessInfoForm> = restorableBusinessInfo(
        JSON.parse(savedData)
      );
      // The field shows a phone the way this form writes one. A session can still hold a raw
      // E.164 number — a draft saved before that was true, or an older tab — and the prefill
      // protects whatever is already there rather than replacing it, so the shape is settled
      // here on the way in instead of by a migration.
      if (parsedData.businessPhone) {
        parsedData.businessPhone = normalizeStoredPhone(
          parsedData.businessPhone
        );
      }
      // Set form data using setValue
      for (const key in parsedData) {
        if (parsedData.hasOwnProperty(key)) {
          setValue(
            key as keyof BusinessInfoForm,
            parsedData[key as keyof BusinessInfoForm]
          );
        }
      }
      // Set carrier type state based on saved data
      if (parsedData?.carrier_type) {
        setCarrierType(parsedData.carrier_type);
      } else if (parsedData?.dot_number) {
        // If DOT number exists but no carrier_type, set to DOT
        setCarrierType('DOT');
        setValue('carrier_type', 'DOT');
      } else {
        // Default to MC if nothing is set
        setCarrierType('MC');
        setValue('carrier_type', 'MC');
      }

      /**
       * Stands in for a real Google Places selection, so it has to mean a real address.
       *
       * This flag is the only thing between a typed string and a genuine dropdown pick — see
       * `InputWithPlacesAPI`, whose validation passes as soon as it has ever been true. A city
       * alone was enough to satisfy it, and now that a registry is a writer of this session key
       * that is a live path: registry addresses are partial all the time, and a partial one would
       * be composed into the bar, marked as selected, and submitted for KYB. Anything short of a
       * complete address leaves the flag alone and the customer is asked for the real one.
       */
      if (isCompleteAddress(parsedData)) {
        setAddressSelectedFromDropdown(true);
      }
    }
    // Both deps are stable for the life of the component — `setValue` by react-hook-form's
    // contract, `carrierNumberNeedsCorrection` because it is a setter-less `useState` read once
    // at mount. Listing them keeps this a mount-once restore while staying honest about what it
    // reads, rather than hiding the read behind a lint suppression.
  }, [setValue, carrierNumberNeedsCorrection]);

  useEffect(() => {
    // react-hook-form keeps unmounted inputs registered by default, so a customer record that
    // arrives after the first render would leave the carrier fields — and their required-field
    // rules — behind, blocking submit on inputs that are no longer on screen. Dropping them also
    // keeps any value restored from session storage out of the submitted form data.
    if (!carrierIdentityAlreadyCollected) return;
    unregister([...CARRIER_IDENTITY_FORM_FIELDS]);
  }, [carrierIdentityAlreadyCollected, unregister]);

  /**
   * Re-fetches the Highway prefill when we hold an MC/DOT but the session does not hold its
   * prefill (THT-711).
   *
   * The prefill lives in sessionStorage, which does not survive a second tab, a restarted
   * browser, or the shared-tab purge — and once the identity is saved the heads-up page that
   * fetched it never renders again, so before this the customer simply met an empty form with no
   * way back to their own data. The marker is what keeps this to one lookup per carrier rather
   * than one per mount, and it is written for a miss as well as a hit.
   *
   * Safe to re-run by construction: `applyCarrierPrefill` finds its own values already in
   * `existing` and changes nothing, and anything the customer has typed outranks both sources.
   * Only fields still EMPTY on the form are pushed in, because the fetch resolves seconds after
   * mount and the customer may well have started typing by then.
   */
  useEffect(() => {
    if (!willRefetchPrefill.current || !carrierLookupKey) return;
    const saved = identityWhenRecordArrived.current;
    const identifier = saved?.mc_number || saved?.dot_number;
    if (!saved || !identifier) return;
    /**
     * Spent the moment this mount COMMITS to the lookup, not when a response comes back.
     *
     * The marker is written by the response, so it can only ever guard the next mount — it says
     * nothing about the window in between, and this effect re-enters on ordinary re-renders (a
     * `refetchUser`, a screen-size change, the factoring radio, the tooltip). Each of those was
     * another upstream lookup for a carrier we were already asking about.
     */
    willRefetchPrefill.current = false;
    const key = carrierLookupKey;

    let abandoned = false;
    // The loading state is capped well below the lookup's own timeout — a slow Highway must not
    // hold a signup form hostage. Past this the fields appear and the fetch keeps going.
    const giveUpWaiting = setTimeout(
      () => setPrefillSettled(true),
      PREFILL_LOOKUP_LOADING_MAX_MS
    );
    (async () => {
      try {
        const prefill = await fetchCarrierPrefillOnce(
          callProtectedApiRef.current,
          identifier,
          saved.carrier_type
        );
        // Marked either way: a carrier Highway does not know must not be looked up again on every
        // mount for the rest of the session.
        markCarrierLookedUp(key);
        if (abandoned || !prefill) return;
        /**
         * A carrier the customer has since moved off must not be applied — the same race the
         * heads-up page guards on its own side, and asked the same way, so a correction started
         * from THIS page cannot be answered by the lookup it replaced.
         */
        if (!carrierPrefillIsCurrent(key)) return;
        /**
         * And again against the record, which is the durable answer: it can be refetched while
         * this is open, and a correction rewrites the carrier on it.
         */
        const current = readSavedCarrierIdentity(userRecordRef.current);
        const currentIdentifier = current?.mc_number || current?.dot_number;
        if (
          current &&
          currentIdentifier &&
          carrierPrefillKey(currentIdentifier, current.carrier_type) !== key
        ) {
          return;
        }
        applyCarrierPrefill(
          prefill,
          buildPrefillAccountSnapshot(userRecordRef.current, {
            email: authIdentityRef.current?.email,
            phoneNumber: authIdentityRef.current?.phone_number,
          }),
          key
        );

        // Read back what the merge actually settled on rather than the raw payload: the account's
        // own values beat Highway inside `applyCarrierPrefill`, and this step must show the same
        // thing the session now holds and the next mount would restore. Through the step's own
        // guarded read, so a session holding unreadable JSON costs an un-prefilled form and not a
        // throw that would strand the customer on the loading state — and through the same
        // one-liner rule as the mount restore, or this would fill the bar the restore had just
        // deliberately left empty.
        const merged = restorableBusinessInfo(readStoredBusinessInfo());
        /**
         * The address goes in as one unit or not at all.
         *
         * Filling only the parts still empty would finish an address the customer already started
         * from a different source: a Places pick refused for its missing street number, then a
         * Highway number added to Places' street, city, state and ZIP. All five parts would then
         * be filled and the submit check would wave through an address nobody chose.
         */
        const addressOnScreen = ADDRESS_FORM_FIELDS.some((name) => {
          const current = getValues(name);
          return current !== undefined && current !== null && current !== '';
        });
        (Object.keys(merged) as (keyof BusinessInfoForm)[]).forEach((name) => {
          const value = merged[name];
          if (value === undefined || value === null || value === '') return;
          if (addressOnScreen && ADDRESS_FORM_FIELDS.includes(name)) return;
          const current = getValues(name);
          // Never over a value on screen — the fetch resolves seconds after mount, by which time
          // the customer may have started typing.
          if (current !== undefined && current !== null && current !== '')
            return;
          setValue(
            name,
            name === 'businessPhone'
              ? (normalizeStoredPhone(value) as never)
              : (value as never)
          );
        });
        // Only a COMPLETE address stands in for a Places selection — see the same gate on the
        // mount restore above, and `isCompleteAddress`.
        if (!addressOnScreen && isCompleteAddress(merged)) {
          setAddressSelectedFromDropdown(true);
        }
      } finally {
        // Last, and in a `finally` so nothing above can strand the customer on a spinner with the
        // timer that would have released them already cleared. The values are in the form before
        // it is on screen: these land in the same tick and React batches them, so the customer's
        // first sight of the form is the filled one — never an empty form that rewrites itself a
        // frame later, which is the flicker this whole loading state exists to remove.
        clearTimeout(giveUpWaiting);
        if (!abandoned) setPrefillSettled(true);
      }
    })();

    return () => {
      abandoned = true;
      clearTimeout(giveUpWaiting);
    };
    // `callProtectedApi`, the customer record and the Auth0 identity are read through refs rather
    // than depended on: `useApi()` builds a NEW `callProtectedApi` on every render, so listing it
    // made every re-render a fresh upstream lookup. `carrierLookupKey` is the one dependency that
    // moves, and it moves exactly once — from null to the carrier, when the record arrives — so a
    // record that shows up after the first render still gets its lookup and nothing else does.
    // The other two are stable by react-hook-form's contract.
  }, [carrierLookupKey, getValues, setValue]);

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="flex h-full w-full flex-col items-center">
            {showPrefillLoader ? (
              /* The re-fetch (THT-711) takes a second or two, and without this the customer met
                 an empty form that rewrote itself underneath them. Same treatment the signup page
                 uses while it looks for their application, so the wait reads as one continuous
                 flow rather than a new kind of pause. */
              <div
                data-testid="prefill-loading"
                className="flex h-full w-full flex-col items-center justify-center py-20 align-middle"
              >
                <img
                  alt=""
                  src="/Icon-v2.gif"
                  className="flex h-44 w-44 justify-center"
                />
                <h2 className="text-[14px] text-secondary">
                  {PREFILL_LOOKUP_LOADING_TEXT}
                </h2>
              </div>
            ) : !loading ? (
              <>
                {/* Main content area with form and sidebar */}
                <div className="w-full flex-1 overflow-y-auto py-10 lg:overflow-visible lg:py-20">
                  <div className="flex w-full justify-center px-6 lg:px-12">
                    <div className="mx-auto flex w-auto flex-col items-center justify-center gap-8 lg:flex-row lg:items-start">
                      {/* Form section */}
                      <div className="mx-auto flex-1 md:w-[600px] lg:w-[800px] lg:max-w-[800px]">
                        <div className="mb-8">
                          <h2 className="mb-2 text-[32px] font-bold text-gray-900">
                            Business Information
                          </h2>
                          <p className="text-lg text-gray-600">
                            Get pre-approved in as quickly as 2 minutes!
                          </p>
                        </div>
                        <form
                          onSubmit={handleSubmit(onSubmit)}
                          className="space-y-6"
                        >
                          <div className="flex w-full flex-col items-start gap-6 align-middle md:flex-row md:gap-4">
                            <div className="w-full md:w-[50%]">
                              <label
                                htmlFor="businessName"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Legal Business Name
                              </label>
                              <input
                                type="text"
                                {...register('businessName', {
                                  required: 'Legal business name is required',
                                })}
                                id="businessName"
                                placeholder="ABC Trucking LLC"
                                className={`mt-1 block w-full border px-3 py-2 ${errors.businessName ? 'border-red-500' : 'border-gray-300'} focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                              />
                              {errors.businessName && (
                                <p className="mt-1 text-sm text-red-500">
                                  {errors.businessName.message}
                                </p>
                              )}
                            </div>

                            <div className="w-full md:w-[50%]">
                              <label
                                htmlFor="businessPhone"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Business Phone
                              </label>
                              <input
                                placeholder="4042223333"
                                type="tel"
                                {...register('businessPhone', {
                                  required: 'Phone number is required',
                                  validate: (value) =>
                                    parseUSPhoneToE164(value)
                                      ? true
                                      : 'Enter a valid US phone number',
                                })}
                                onChange={(e) => {
                                  // Format as the user types. Only validate once the number is
                                  // actually valid so we don't show (or leave stale) an "invalid"
                                  // message while they're still typing.
                                  const formatted = formatUSPhonePretty(
                                    e.target.value
                                  );
                                  const isValid =
                                    !!parseUSPhoneToE164(formatted);
                                  setValue('businessPhone', formatted, {
                                    shouldValidate: isValid,
                                  });
                                  if (!isValid) {
                                    clearErrors('businessPhone');
                                  }
                                }}
                                id="businessPhone"
                                className={`mt-1 block w-full border px-3 py-2 ${errors.businessPhone ? 'border-red-500' : 'border-gray-300'} focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                              />
                              {errors.businessPhone && (
                                <p className="mt-1 text-sm text-red-500">
                                  {errors.businessPhone.message}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="w-full">
                            <FlexibleAddressInput
                              label="Legal Business Address"
                              searchFieldName="businessAddress"
                              streetNumberFieldName="address_st_number"
                              streetNameFieldName="address_st_name"
                              unitFieldName="address_unit"
                              cityFieldName="address_city"
                              stateFieldName="address_state"
                              zipFieldName="address_zip"
                              register={register}
                              control={control}
                              watch={watch}
                              setValue={setValue}
                              errors={errors}
                              initialMode="autocomplete"
                              requireCompleteAddress
                              searchPlaceholder="Enter legal business address"
                              addressSelectedFromDropdown={
                                addressSelectedFromDropdown
                              }
                              setAddressSelectedFromDropdown={
                                setAddressSelectedFromDropdown
                              }
                              cookieKey="businessAddressMode"
                            />
                          </div>
                          <div className="flex w-full flex-col items-start gap-6 align-middle md:flex-row md:gap-4">
                            <div className="w-full md:w-[50%]">
                              <label
                                htmlFor="taxId"
                                className={`${screenView === 'desktop' ? 'flex items-center' : 'block'} text-sm font-medium text-gray-700`}
                              >
                                Employer Identification Number (EIN)
                                {screenView === 'desktop' && (
                                  <SensitiveFieldHelp fieldType="EIN" />
                                )}
                              </label>
                              <input
                                type="text"
                                {...register('taxId', {
                                  required: 'EIN is required',
                                  validate: (value) => {
                                    // Only validate complete EINs or empty values
                                    if (
                                      !value ||
                                      value.match(/^[0-9]{2}-[0-9]{7}$/)
                                    ) {
                                      return true;
                                    }
                                    return 'Invalid EIN, must be in the format XX-XXXXXXX';
                                  },
                                  onChange: (e) => {
                                    // Remove all non-digits
                                    const digits = e.target.value.replace(
                                      /[^0-9]/g,
                                      ''
                                    );

                                    // Format as XX-XXXXXXX
                                    if (digits.length <= 2) {
                                      e.target.value = digits;
                                    } else {
                                      // Ensure we don't exceed the expected length
                                      const firstPart = digits.slice(0, 2);
                                      const secondPart = digits.slice(2, 9);
                                      e.target.value = `${firstPart}-${secondPart}`;
                                    }
                                  },
                                  onBlur: (e) => {
                                    // Final validation on blur to ensure format is correct
                                    const value = e.target.value;
                                    if (
                                      value &&
                                      !value.match(/^[0-9]{2}-[0-9]{7}$/)
                                    ) {
                                      // Clear or fix invalid formats when focus leaves the field
                                      if (
                                        !value.includes('-') &&
                                        value.length >= 9
                                      ) {
                                        e.target.value = `${value.slice(0, 2)}-${value.slice(2, 9)}`;
                                      }
                                    }
                                  },
                                })}
                                id="taxId"
                                className={`mt-1 block w-full border px-3 py-2 ${errors.taxId ? 'border-red-500' : 'border-gray-300'} focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                                placeholder="XX-XXXXXXX"
                              />
                              {errors.taxId && (
                                <p className="mt-1 text-sm text-red-500">
                                  {errors.taxId.message}
                                </p>
                              )}
                              {screenView !== 'desktop' && (
                                <SensitiveFieldHelp fieldType="EIN" />
                              )}
                            </div>

                            <div className="w-full md:w-[50%]">
                              <label
                                htmlFor="annualRevenue"
                                className="block text-sm font-medium text-gray-700"
                              >
                                Average Annual Revenue in USD
                                {screenView === 'desktop' && (
                                  <SensitiveFieldHelp fieldType="AVERAGE_ANNUAL_REVENUE" />
                                )}
                              </label>
                              <Controller
                                name="annualRevenue"
                                control={control}
                                rules={{
                                  required: 'This field is required',
                                  min: {
                                    value: 0,
                                    message:
                                      'Revenue must be a positive number',
                                  },
                                }}
                                render={({ field }) => (
                                  <InputNumber
                                    id="annualRevenue"
                                    className={`mt-1 flex h-[38px] min-h-[38px] w-full items-center rounded-none text-center ${errors.annualRevenue ? 'border-red-500' : 'border-gray-300'}`}
                                    formatter={(v) => formatCurrencyForAntd(v)}
                                    parser={(v) => parseCurrencyFromAntd(v)}
                                    min={0}
                                    controls={false}
                                    {...field}
                                  />
                                )}
                              />
                              {errors.annualRevenue && (
                                <p className="mt-1 text-sm text-red-500">
                                  {errors.annualRevenue.message}
                                </p>
                              )}
                              {screenView !== 'desktop' && (
                                <SensitiveFieldHelp fieldType="AVERAGE_ANNUAL_REVENUE" />
                              )}
                            </div>
                          </div>

                          {/* Factoring Company Question (relocated from the removed Business Debt
                              step; grouped with the financial fields above). */}
                          <div className="flex w-full flex-col items-start gap-6 align-middle md:flex-row md:gap-4">
                            <div className="w-full md:w-[50%]">
                              <label className="block text-sm font-medium text-gray-700">
                                Do you use a factoring company?
                              </label>
                              <Controller
                                name="uses_factoring"
                                control={control}
                                defaultValue="yes"
                                render={({ field }) => (
                                  <Radio.Group
                                    value={field.value}
                                    onChange={(e) => {
                                      field.onChange(e.target.value);
                                      if (e.target.value === 'no') {
                                        setValue(
                                          'factoring_company_name',
                                          undefined
                                        );
                                      }
                                    }}
                                    className="mt-1 flex w-full gap-3"
                                  >
                                    {[
                                      { label: 'Yes', value: 'yes' },
                                      { label: 'No', value: 'no' },
                                    ].map((opt) => (
                                      <Radio.Button
                                        key={opt.value}
                                        value={opt.value}
                                        className="!flex !h-[38px] !flex-1 !items-center !justify-center !rounded-[4px] !border !border-bordergray !bg-white !font-medium !text-primary !shadow-sm !transition-all hover:!border-accent hover:!text-accent [&.ant-radio-button-wrapper-checked]:!border-accent [&.ant-radio-button-wrapper-checked]:!bg-accent [&.ant-radio-button-wrapper-checked]:!text-white [&.ant-radio-button-wrapper-checked]:hover:!bg-accent [&.ant-radio-button-wrapper::before]:!hidden"
                                      >
                                        {opt.label}
                                      </Radio.Button>
                                    ))}
                                  </Radio.Group>
                                )}
                              />
                            </div>

                            {watch('uses_factoring') !== 'no' && (
                              <div className="w-full md:w-[50%]">
                                <label
                                  htmlFor="factoring_company_name"
                                  className="block text-sm font-medium text-gray-700"
                                >
                                  Factoring Company Name
                                </label>
                                <input
                                  type="text"
                                  {...register('factoring_company_name', {
                                    required:
                                      watch('uses_factoring') !== 'no'
                                        ? 'Please enter your factoring company name'
                                        : false,
                                  })}
                                  id="factoring_company_name"
                                  placeholder="Enter the name of your factoring company"
                                  className={`mt-1 block w-full border px-3 py-2 ${errors.factoring_company_name ? 'border-red-500' : 'border-gray-300'} focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                                />
                                {errors.factoring_company_name && (
                                  <p className="mt-1 text-sm text-red-500">
                                    {errors.factoring_company_name.message}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>

                          <div className="flex w-full flex-col items-start gap-6 align-middle md:flex-row md:gap-4">
                            <div className="w-full md:w-[50%]">
                              <label
                                htmlFor="yearsInBusiness"
                                className="block text-sm font-medium text-gray-700"
                              >
                                How many years have you been in business?
                              </label>
                              <input
                                onKeyDown={(e) => {
                                  if (e.key === '-' || e.key === 'e') {
                                    e.preventDefault();
                                  }
                                }}
                                min={0}
                                type="number"
                                {...register('yearsInBusiness', {
                                  required: 'This field is required',
                                  min: {
                                    value: 0,
                                    message: 'Years must be a positive number',
                                  },
                                })}
                                id="yearsInBusiness"
                                className={`mt-1 block w-full border px-3 py-2 ${
                                  errors.yearsInBusiness
                                    ? 'border-red-500'
                                    : 'border-gray-300'
                                } focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                              />
                              {errors.yearsInBusiness && (
                                <p className="mt-1 text-sm text-red-500">
                                  {errors.yearsInBusiness.message}
                                </p>
                              )}
                            </div>
                            <div className="w-full md:w-[50%]">
                              <label
                                htmlFor="state_of_registration"
                                className="block text-sm font-medium text-gray-700"
                              >
                                What state did you register your business in?
                              </label>
                              <Controller
                                name="state_of_registration"
                                control={control}
                                rules={{ required: 'Please select a state' }}
                                render={({ field }) => (
                                  <Select
                                    id="state_of_registration"
                                    className={`mt-1 block h-[38px] min-h-[38px] w-full ${
                                      errors.state_of_registration
                                        ? 'border-red-500'
                                        : 'border-gray-300'
                                    }`}
                                    style={{
                                      borderRadius: 0,
                                    }}
                                    placeholder="Select a state"
                                    options={US_STATES.map((state) => ({
                                      value: state.abbreviation,
                                      label: state.name,
                                    }))}
                                    {...field}
                                  />
                                )}
                              />
                              {errors.state_of_registration && (
                                <p className="mt-1 text-sm text-red-500">
                                  {errors.state_of_registration.message}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* The lookup-failure notice points at the carrier number field
                              below it, so it goes wherever that field goes. */}
                          {!carrierIdentityAlreadyCollected &&
                            carrierNumberWarning && (
                              <div className="mb-4 w-full rounded-md border border-yellow-200 bg-yellow-50 p-3">
                                <div className="flex items-start">
                                  <div className="flex-shrink-0">
                                    <svg
                                      className="h-5 w-5 text-yellow-400"
                                      xmlns="http://www.w3.org/2000/svg"
                                      viewBox="0 0 20 20"
                                      fill="currentColor"
                                    >
                                      <path
                                        fillRule="evenodd"
                                        d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
                                        clipRule="evenodd"
                                      />
                                    </svg>
                                  </div>
                                  <div className="ml-3">
                                    <h3 className="text-sm font-medium text-yellow-800">
                                      {carrierNumberLabel} Not Found
                                    </h3>
                                    <div className="mt-1 text-sm text-yellow-700">
                                      <p>
                                        Please double-check the{' '}
                                        {carrierNumberLabel} below or try again
                                        with your {otherCarrierNumberLabel}.
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          {/* THT-711: the slim-down takes the carrier block away once the
                              heads-up page has the answer, which left a saved MC/DOT with no way
                              back — the form that used to fix a typo no longer asks. So what we
                              hold is read back here, and the link hands the customer to the page
                              that collects it, where a new number gets the same carrier lookup,
                              confirmation and prefill the first one did. Legacy records reach
                              this having never been shown their number for confirmation at all,
                              which is exactly who needs the way back. */}
                          {carrierIdentityAlreadyCollected &&
                            savedIdentity &&
                            onEditCarrierIdentity && (
                              <div className="w-full rounded-[4px] border border-bordergray bg-primarybg px-4 py-3">
                                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                                  <p className="text-sm text-gray-600">
                                    {savedIdentityLabel}{' '}
                                    <span className="font-medium text-primary">
                                      {savedIdentityValue}
                                    </span>
                                  </p>
                                  <button
                                    type="button"
                                    onClick={onEditCarrierIdentity}
                                    className="text-sm font-[700] text-accent underline"
                                  >
                                    Not right?
                                  </button>
                                </div>
                              </div>
                            )}

                          <div className="flex w-full flex-col items-start gap-6 align-middle md:flex-row md:gap-4">
                            {!carrierIdentityAlreadyCollected && (
                              <>
                                <div className="w-full md:w-[50%]">
                                  <label
                                    htmlFor="carrier_type"
                                    className="block text-sm font-medium text-gray-700"
                                  >
                                    Carrier Number Type
                                  </label>
                                  <Controller
                                    name="carrier_type"
                                    control={control}
                                    defaultValue="MC"
                                    render={({ field }) => (
                                      <Select
                                        id="carrier_type"
                                        className="mt-1 block h-[38px] min-h-[38px] w-full"
                                        style={{
                                          borderRadius: 0,
                                        }}
                                        placeholder="Select carrier type"
                                        options={[
                                          { value: 'MC', label: 'MC Number' },
                                          { value: 'DOT', label: 'DOT Number' },
                                        ]}
                                        {...field}
                                        onChange={(value) => {
                                          field.onChange(value);
                                          setCarrierType(value as 'MC' | 'DOT');
                                          // Clear the warning when user changes the carrier type
                                          setCarrierNumberWarning(false);
                                          // Clear the other field when switching
                                          if (value === 'MC') {
                                            setValue('dot_number', undefined);
                                          } else {
                                            setValue('mc_number', undefined);
                                          }
                                        }}
                                      />
                                    )}
                                  />
                                </div>
                                <div className="w-full md:w-[50%]">
                                  <label
                                    htmlFor={
                                      carrierType === 'MC'
                                        ? 'mc_number'
                                        : 'dot_number'
                                    }
                                    className="block text-sm font-medium text-gray-700"
                                  >
                                    {carrierType === 'MC'
                                      ? 'MC Number'
                                      : 'DOT Number'}
                                  </label>
                                  <input
                                    key={carrierType}
                                    type="text"
                                    {...register(
                                      carrierType === 'MC'
                                        ? 'mc_number'
                                        : 'dot_number',
                                      {
                                        validate: (value) => {
                                          if (!value)
                                            return 'This field is required';

                                          if (carrierType === 'MC') {
                                            // MC Number: 1-8 digits
                                            if (value.match(/^[0-9]{1,8}$/)) {
                                              return true;
                                            }
                                            return 'MC Number must be 1-8 digits';
                                          } else {
                                            // DOT Number: 1-10 digits
                                            if (value.match(/^[0-9]{1,10}$/)) {
                                              return true;
                                            }
                                            return 'DOT Number must be 1-10 digits';
                                          }
                                        },
                                        onChange: (e) => {
                                          // Clear the warning when user starts typing
                                          setCarrierNumberWarning(false);
                                          // Remove all non-digits and limit based on type
                                          const digits = e.target.value.replace(
                                            /[^0-9]/g,
                                            ''
                                          );
                                          const maxLength =
                                            carrierType === 'MC' ? 8 : 10;
                                          e.target.value = digits.slice(
                                            0,
                                            maxLength
                                          );
                                        },
                                      }
                                    )}
                                    id={
                                      carrierType === 'MC'
                                        ? 'mc_number'
                                        : 'dot_number'
                                    }
                                    placeholder={
                                      carrierType === 'MC'
                                        ? '1234567'
                                        : '12345678'
                                    }
                                    maxLength={carrierType === 'MC' ? 8 : 10}
                                    className={`mt-1 block w-full border px-3 py-2 ${(carrierType === 'MC' ? errors.mc_number : errors.dot_number) ? 'border-red-500' : 'border-gray-300'} focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                                  />
                                  {(carrierType === 'MC'
                                    ? errors.mc_number
                                    : errors.dot_number) && (
                                    <p className="mt-1 text-sm text-red-500">
                                      {carrierType === 'MC'
                                        ? errors.mc_number?.message
                                        : errors.dot_number?.message}
                                    </p>
                                  )}
                                </div>
                              </>
                            )}

                            {/* Half the row while it sits beside the carrier inputs; the whole
                                row once the slim-down takes those away, since a lone field pinned
                                to 50% leaves the other half visibly empty. */}
                            <div
                              className={
                                carrierIdentityAlreadyCollected
                                  ? 'w-full'
                                  : 'w-full md:w-[50%]'
                              }
                            >
                              <div className="relative flex items-center">
                                <label
                                  htmlFor="referralCode"
                                  className="block text-sm font-medium text-gray-700"
                                >
                                  Referral Code (optional)
                                </label>
                                <Tooltip
                                  show={!!showTooltip}
                                  message="Optional. If you received a referral code from a marketing campaign or your logistics broker, enter it here."
                                  className="bottom-8 left-[22px]"
                                  position="top"
                                />
                                <div
                                  onMouseEnter={() => setShowTooltip(true)}
                                  onMouseLeave={() => setShowTooltip(false)}
                                  className="flex cursor-help items-center"
                                >
                                  <Question
                                    size={16}
                                    className="ml-1 text-gray-400"
                                  />
                                </div>
                              </div>

                              <input
                                type="text"
                                {...register('referralCode', {})}
                                id="referralCode"
                                className={`mt-1 block w-full border px-3 py-2 ${errors.referralCode ? 'border-red-500' : 'border-gray-300'} focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                              />
                              {errors.referralCode && (
                                <p className="mt-1 text-sm text-red-500">
                                  {errors.referralCode.message}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* MC/DOT Ownership Question */}
                          {!carrierIdentityAlreadyCollected && (
                            <div className="mt-8 w-full">
                              <label className="mb-2 block text-sm font-medium text-gray-700">
                                Are you running under your own {carrierType}{' '}
                                number?
                              </label>
                              <Controller
                                name="owns_their_mc_dot_number"
                                control={control}
                                rules={{
                                  validate: (value) =>
                                    typeof value === 'boolean' ||
                                    `Please select whether you own this ${carrierType} number`,
                                }}
                                render={({ field }) => (
                                  <div className="mt-1 flex flex-col gap-3 sm:flex-row">
                                    <label
                                      className={`flex flex-1 cursor-pointer items-center gap-3 rounded-[4px] border px-4 py-3 shadow-sm transition-all ${
                                        field.value === true
                                          ? 'border-accent ring-1 ring-accent'
                                          : 'border-bordergray hover:border-accent'
                                      }`}
                                    >
                                      <input
                                        type="radio"
                                        value="true"
                                        checked={field.value === true}
                                        onChange={() => {
                                          field.onChange(true);
                                          setValue(
                                            'other_mc_dot_number_owner_name',
                                            undefined
                                          );
                                        }}
                                        className="h-4 w-4 accent-accent"
                                      />
                                      <span className="text-sm text-primary">
                                        Yes, this {carrierType} Number is mine
                                      </span>
                                    </label>
                                    <label
                                      className={`flex flex-1 cursor-pointer items-center gap-3 rounded-[4px] border px-4 py-3 shadow-sm transition-all ${
                                        field.value === false
                                          ? 'border-accent ring-1 ring-accent'
                                          : 'border-bordergray hover:border-accent'
                                      }`}
                                    >
                                      <input
                                        type="radio"
                                        value="false"
                                        checked={field.value === false}
                                        onChange={() => field.onChange(false)}
                                        className="h-4 w-4 accent-accent"
                                      />
                                      <span className="text-sm text-primary">
                                        No, this {carrierType} Number is someone
                                        else's
                                      </span>
                                    </label>
                                  </div>
                                )}
                              />
                              {errors.owns_their_mc_dot_number && (
                                <p className="mt-1 text-sm text-red-500">
                                  {errors.owns_their_mc_dot_number.message}
                                </p>
                              )}

                              {/* Owner Name Input */}
                              {watch('owns_their_mc_dot_number') === false && (
                                <div className="mt-8">
                                  <label
                                    htmlFor="other_mc_dot_number_owner_name"
                                    className="block text-sm font-medium text-gray-700"
                                  >
                                    Who owns this {carrierType} number?
                                  </label>
                                  <input
                                    type="text"
                                    {...register(
                                      'other_mc_dot_number_owner_name',
                                      {
                                        required:
                                          watch('owns_their_mc_dot_number') ===
                                          false
                                            ? `Please specify who owns the ${carrierType} number`
                                            : false,
                                        maxLength: {
                                          value: 400,
                                          message:
                                            'Owner name must be less than 400 characters',
                                        },
                                      }
                                    )}
                                    id="other_mc_dot_number_owner_name"
                                    placeholder="Enter the owner's name"
                                    maxLength={400}
                                    className={`mt-1 block w-full border px-3 py-2 ${errors.other_mc_dot_number_owner_name ? 'border-red-500' : 'border-gray-300'} focus:border-blue-400 focus:outline-none focus:ring-blue-400 sm:text-sm`}
                                  />
                                  {errors.other_mc_dot_number_owner_name && (
                                    <p className="mt-1 text-sm text-red-500">
                                      {
                                        errors.other_mc_dot_number_owner_name
                                          .message
                                      }
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          <SignupFooter
                            selectedRole={selectedRole}
                            currStep={currStep}
                            isMobile={false}
                          />
                        </form>
                      </div>

                      {/* Desktop sidebar with trust signals */}
                      {screenView === 'desktop' ? (
                        <div className="sticky top-8 hidden h-fit w-[350px] lg:block">
                          <TrustSignals variant="sidebar" />
                        </div>
                      ) : (
                        <div className="mb-4 max-w-[600px]">
                          <TrustSignals variant="mobile" />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <ClipLoader
                  color="#1D213B"
                  loading={loading}
                  size={60}
                  aria-label="Loading Spinner"
                  data-testid="loader"
                  className="h-full w-full items-center justify-center pt-10"
                />
              </div>
            )}
          </div>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default BusinessInfo;
